import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { SampleFeedbackGradeEntry } from "@repo/app-types/entities/playbookProposalDetail";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { getAccuracyClassificationForGradingValue } from "@repo/app-types/helpers/feedbackHelpers";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";
import { FeedbackCell } from "./graderFeedback/feedbackCell.js";

interface FeedbackGradingsTableProps {
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
  entries: readonly SampleFeedbackGradeEntry[];
  isLoading?: boolean;
  isError?: boolean;
  onGradingSelect: (gradingId: GradingId) => void;
}

type FeedbackGradingRow = SampleFeedbackGradeEntry & { id: string };

export const FeedbackGradingsTable: FC<FeedbackGradingsTableProps> = (
  props,
) => {
  const { gradingValueType, entries } = props;

  const rows = useMemo(
    (): FeedbackGradingRow[] =>
      entries.map((e) => ({ ...e, id: e.feedback_id })),
    [entries],
  );

  const columns = useMemo(
    (): EntityTableColumnDefinition<FeedbackGradingRow>[] => [
      {
        id: "date",
        header: "Date",
        isRowHeader: true,
        renderCell: (entry) =>
          new Date(entry.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
      },
      {
        id: "submittedBy",
        header: "Submitted by",
        renderCell: (entry) => (
          <span className="text-gray-600">
            {entry.feedback_author_name ?? "-"}
          </span>
        ),
      },
      {
        id: "currentGrade",
        header: "Current grade",
        renderCell: (entry) => {
          if (!entry.current_grade) {
            return <span className="text-gray-400">-</span>;
          }
          const accuracy = getAccuracyClassificationForGradingValue(
            gradingValueType,
            entry.current_grade.value,
            entry.correct_grade.value,
          );
          return (
            <FeedbackCell
              value={entry.current_grade.value}
              commentary={entry.current_grade.commentary}
              correctness={
                accuracy !== undefined
                  ? {
                      isCorrect: accuracy === "correct",
                      isClose: accuracy === "close",
                    }
                  : undefined
              }
            />
          );
        },
      },
      {
        id: "testGrade",
        header: "Test grade",
        renderCell: (entry) => {
          if (!entry.test_grade) {
            return <span className="text-gray-400">-</span>;
          }
          const accuracy = getAccuracyClassificationForGradingValue(
            gradingValueType,
            entry.test_grade.value,
            entry.correct_grade.value,
          );
          return (
            <FeedbackCell
              value={entry.test_grade.value}
              commentary={entry.test_grade.commentary}
              correctness={
                accuracy !== undefined
                  ? {
                      isCorrect: accuracy === "correct",
                      isClose: accuracy === "close",
                    }
                  : undefined
              }
            />
          );
        },
      },
      {
        id: "correctGrade",
        header: "Correct grade",
        renderCell: (entry) => (
          <FeedbackCell
            value={entry.correct_grade.value}
            commentary={entry.correct_grade.commentary}
          />
        ),
      },
    ],
    [gradingValueType],
  );

  return (
    <EntityTable
      aria-label="Feedback graded calls"
      columns={columns}
      entities={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      emptyMessage="No feedback calls found."
      size={Size.SMALL}
      isFullBleed
      onEntityAction={(entry) => props.onGradingSelect(entry.grading_id)}
      getRowClassName={alignCellsTop}
    />
  );
};

function alignCellsTop(): string {
  return "relative align-top";
}
